import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { moreNavigation } from '../lib/navigation';
import { navigationStyle, navigationOnPath } from '../lib/navigationIdentity';
import { useI18n } from '../i18n';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import ExperienceSwitcher from './ExperienceSwitcher';

export default function MoreMenu({ open, onClose }) {
    const { t } = useI18n();
    const { experience } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const sheet = useRef(null);
    useEffect(() => {
        if (!open) return;
        const previous = document.activeElement;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        sheet.current?.querySelector('button')?.focus();
        const onKey = e => {
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            if (e.key !== 'Tab') return;
            const controls = sheet.current?.querySelectorAll('button:not([disabled]), a[href], input, select, [tabindex="0"]');
            if (!controls?.length) return;
            const first = controls[0], last = controls[controls.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflow;
            if (previous?.isConnected) previous.focus();
        };
    }, [open, onClose]);
    if (!open) return null;

    // A personal log must not silently open the community map for a guest.
    const go = (to, requiresMember) => {
        onClose();
        navigate(requiresMember && (!user || user.is_guest) ? '/login' : to);
    };

    return (
        <div
            className="fixed inset-0 z-[70] bg-brand-ink/40 backdrop-blur-sm flex items-end sm:items-center justify-center"
            onClick={onClose}
            data-testid="more-menu-overlay"
        >
            <div
                role="dialog"
                ref={sheet}
                id="growdaily-more-menu"
                aria-modal="true"
                aria-labelledby="more-menu-title"
                className="bg-brand-bg w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-brand-border max-h-[85vh] overflow-y-auto animate-fade-up"
                onClick={(e) => e.stopPropagation()}
                data-testid="more-menu-sheet"
            >
                <div className="sticky top-0 bg-brand-bg/95 backdrop-blur px-6 pt-6 pb-3 flex items-start justify-between">
                    <div>
                        <h2 id="more-menu-title" className="font-serif text-2xl text-brand-ink">{t('menu.title')}</h2>
                        <p className="text-xs text-brand-ink3 mt-0.5">{t('nav.more_sub')}</p>
                    </div>
                    <button type="button" aria-label={t('nav.close_menu')} data-testid="more-menu-close" onClick={onClose} className="p-2 hover:bg-brand-bg2 rounded-full -mr-2"><X size={18} aria-hidden="true" /></button>
                </div>
                <div className="gd-more-experience">
                    <ExperienceSwitcher />
                    <p>{t('experience.description')}</p>
                </div>
                <div className="gd-more-groups">
                    {moreNavigation(experience).map(group => (
                        <section key={group.key} aria-label={t(group.key)}>
                            <h3 className="gd-more-heading">{t(group.key)}</h3>
                            <div className="gd-more-grid">
                                {group.items.map(({ to, icon: Icon, key, testid, id, requiresMember }) => (
                                    <button type="button" key={to} data-testid={testid} data-nav-function={id} style={navigationStyle(to)}
                                        aria-current={navigationOnPath(pathname, to) ? 'page' : undefined} onClick={() => go(to, requiresMember)} className={`gd-more-item ${to === '/eden' ? 'is-featured' : ''}`}>
                                        <span className="gd-glyph gd-semantic-glyph" aria-hidden="true"><Icon strokeWidth={1.8} /></span>
                                        <span className="gd-more-label">{t(key)}</span>
                                        {to === '/eden' && <span className="gd-more-play" aria-hidden="true">▶</span>}
                                    </button>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}
